import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Trello API config
const TRELLO_CONFIG = {
  boardId: "697ab07aa257c66a14d1cad9",
  apiKey: "976ff84fd483e371bff82da266f31a35",
  token:
    "ATTA30c4de8bbd4d03f91d2759238921a25352ddcfaf8f030c1a2029d23407e4b7de0E8743AF",
};

const baseUrl = "https://api.trello.com/1";
const auth = `key=${TRELLO_CONFIG.apiKey}&token=${TRELLO_CONFIG.token}`;

// Map Trello list names to status values
const LIST_TO_STATUS = {
  Backlog: "Not Started",
  "Up Next": "Up Next",
  "In Progress": "In Progress",
  Done: "Completed",
};

export const queryProjects = createAsyncThunk(
  "projects/query",
  async (_, { rejectWithValue }) => {
    try {
      // Fetch lists
      const listsRes = await fetch(
        `${baseUrl}/boards/${TRELLO_CONFIG.boardId}/lists?${auth}`
      );
      if (!listsRes.ok) throw new Error("Failed to fetch lists");
      const lists = await listsRes.json();

      // Fetch cards with members and labels
      const cardsRes = await fetch(
        `${baseUrl}/boards/${TRELLO_CONFIG.boardId}/cards?members=true&member_fields=fullName,initials&${auth}`
      );
      if (!cardsRes.ok) throw new Error("Failed to fetch cards");
      const cards = await cardsRes.json();

      // Create list ID to name mapping
      const listMap = {};
      lists.forEach((list) => {
        listMap[list.id] = list.name;
      });

      // Transform cards to projects format
      const projects = {};
      cards.forEach((card) => {
        const listName = listMap[card.idList] || "Unknown";
        projects[card.id] = {
          id: card.id,
          title: card.name,
          description: card.desc || "",
          status: LIST_TO_STATUS[listName] || listName,
          listName,
          due: card.due,
          labels: card.labels || [],
          members: card.members || [],
          url: card.url,
          dateLastActivity: card.dateLastActivity,
        };
      });

      return projects;
    } catch (error) {
      console.error("Trello fetch error:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const addProject = createAsyncThunk(
  "projects/add",
  async ({ title, description, status, cb }, { rejectWithValue }) => {
    try {
      // Find the list ID for the status
      const listsRes = await fetch(
        `${baseUrl}/boards/${TRELLO_CONFIG.boardId}/lists?${auth}`
      );
      const lists = await listsRes.json();

      // Map status back to list name
      const statusToList = Object.entries(LIST_TO_STATUS).reduce(
        (acc, [k, v]) => {
          acc[v] = k;
          return acc;
        },
        {}
      );

      const targetListName = statusToList[status] || "Backlog";
      const targetList =
        lists.find((l) => l.name === targetListName) || lists[0];

      // Create card
      const res = await fetch(`${baseUrl}/cards?${auth}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idList: targetList.id,
          name: title,
          desc: description,
        }),
      });

      if (!res.ok) throw new Error("Failed to create card");
      const card = await res.json();

      if (cb) cb();

      return {
        id: card.id,
        title: card.name,
        description: card.desc || "",
        status: LIST_TO_STATUS[targetList.name] || targetList.name,
        listName: targetList.name,
      };
    } catch (error) {
      console.error("Trello add error:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateProject = createAsyncThunk(
  "projects/update",
  async ({ id, values, cb }, { rejectWithValue }) => {
    try {
      // Find the list ID for the new status
      const listsRes = await fetch(
        `${baseUrl}/boards/${TRELLO_CONFIG.boardId}/lists?${auth}`
      );
      const lists = await listsRes.json();

      const statusToList = Object.entries(LIST_TO_STATUS).reduce(
        (acc, [k, v]) => {
          acc[v] = k;
          return acc;
        },
        {}
      );

      const targetListName = statusToList[values.status] || "Backlog";
      const targetList =
        lists.find((l) => l.name === targetListName) || lists[0];

      // Update card
      const res = await fetch(`${baseUrl}/cards/${id}?${auth}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.title,
          desc: values.description,
          idList: targetList.id,
        }),
      });

      if (!res.ok) throw new Error("Failed to update card");

      if (cb) cb();

      return { id, ...values, listName: targetList.name };
    } catch (error) {
      console.error("Trello update error:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProject = createAsyncThunk(
  "projects/delete",
  async ({ id, cb }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${baseUrl}/cards/${id}?${auth}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Failed to delete card");

      if (cb) cb();

      return id;
    } catch (error) {
      console.error("Trello delete error:", error);
      return rejectWithValue(error.message);
    }
  }
);

const slice = createSlice({
  name: "projects",
  initialState: {
    projects: {},
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(queryProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(queryProjects.fulfilled, (state, action) => {
        state.projects = action.payload;
        state.loading = false;
      })
      .addCase(queryProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addProject.fulfilled, (state, action) => {
        state.projects[action.payload.id] = action.payload;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.projects[action.payload.id] = {
          ...state.projects[action.payload.id],
          ...action.payload,
        };
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        delete state.projects[action.payload];
      });
  },
});

export default slice.reducer;
